import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Close as CloseIcon, GetApp as GetAppIcon } from '@mui/icons-material';

interface AppBannerProps {
  // Customizable colors - you can pass these as props to match your theme
  gradientColors?: [string, string];
  textColor?: string;
  buttonBackgroundColor?: string;
  buttonHoverColor?: string;
}

const AppBanner: React.FC<AppBannerProps> = ({
  gradientColors = ['#FF4081', '#9C27B0'], // Default pink to purple
  textColor = 'white',
  buttonBackgroundColor = 'rgba(255,255,255,0.2)',
  buttonHoverColor = 'rgba(255,255,255,0.3)',
}) => {
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Check if user came from mobile app sharing
    const urlParams = new URLSearchParams(window.location.search);
    const fromApp = urlParams.get('from') === 'app';
    
    // Check if banner was previously dismissed (stored in sessionStorage)
    const wasDismissed = sessionStorage.getItem('appBannerDismissed') === 'true';
    
    if (fromApp && !wasDismissed) {
      setShowBanner(true);
    }
  }, []);

  const handleOpenApp = () => {
    // Try to open the app with custom URL scheme
    const appUrl = 'lsnto://';
    window.location.href = appUrl;
    
    // Fallback to app stores after a short delay
    setTimeout(() => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      
      if (isIOS) {
        // Use your actual Apple App Store ID
        window.open('https://apps.apple.com/app/id6745908630', '_blank');
      } else if (isAndroid) {
        // Use your actual Google Play Store package name
        window.open('https://play.google.com/store/apps/details?id=com.lsnto.lsnto_mobile', '_blank');
      } else {
        // Desktop fallback - could link to a landing page or show both options
        window.open('https://apps.apple.com/app/id6745908630', '_blank');
      }
    }, 2000);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    // Remember dismissal for this session
    sessionStorage.setItem('appBannerDismissed', 'true');
  };

  if (!showBanner || dismissed) {
    return null;
  }

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
        color: textColor,
        padding: isMobile ? '12px 16px' : '16px 24px',
        display: 'flex',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}
    >
      {/* App Icon */}
      <Box
        component="img"
        src="/favicon/web-app-manifest-192x192.png" // Using your actual app icon
        alt="Lsnto"
        sx={{
          width: isMobile ? 32 : 40,
          height: isMobile ? 32 : 40,
          borderRadius: 1,
          marginRight: 2,
          flexShrink: 0,
        }}
      />
      
      {/* Banner Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography 
          variant={isMobile ? "body2" : "body1"} 
          sx={{ 
            fontWeight: 600, 
            lineHeight: 1.2,
            marginBottom: isMobile ? 0 : 0.5,
            color: textColor,
          }}
        >
          Get the full Lsnto experience!
        </Typography>
        {!isMobile && (
          <Typography variant="caption" sx={{ opacity: 0.9, color: textColor }}>
            Offline playback, better performance & more features
          </Typography>
        )}
      </Box>
      
      {/* Open App Button */}
      <Button
        variant="contained"
        size={isMobile ? "small" : "medium"}
        startIcon={<GetAppIcon />}
        onClick={handleOpenApp}
        sx={{
          backgroundColor: buttonBackgroundColor,
          border: '1px solid rgba(255,255,255,0.3)',
          color: textColor,
          marginRight: 1,
          '&:hover': {
            backgroundColor: buttonHoverColor,
          },
          fontSize: isMobile ? '0.75rem' : '0.875rem',
          minWidth: isMobile ? 'auto' : '120px',
        }}
      >
        {isMobile ? 'Open' : 'Open App'}
      </Button>
      
      {/* Dismiss Button */}
      <IconButton
        size="small"
        onClick={handleDismiss}
        sx={{
          color: textColor,
          padding: 0.5,
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default AppBanner; 